import * as React from 'react'

import { Box, Button, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import type AttendancePolicy from 'modules/attendance/store/models/AttendancePolicy'

interface Props {
  policy: AttendancePolicy
  showDeleteModal: () => void
}

const submitSetAsDefault = (policy: AttendancePolicy) => async () => {
  await policy.rpc('set_as_default')
  attendancePolicies.fetch()
}

const PolicyCard = ({ policy, showDeleteModal }: Props) => {
  const attendanceSetting = attendanceSettings.currentByPolicy(policy.attributes.id)
  const { navigation } = useNavigation()
  const routeParams = {
    id: 'time_tracking',
    settingId: attendanceSetting?.attributes.id.toString() || 'default',
    tabId: 'edit',
  }
  const route = navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams)

  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Box
      aria-label="Time Tracking Policy"
      flexDirection="row"
      justifyContent="spaceBetween"
      width="full"
    >
      <Box flexDirection="column" gap="s12">
        <Box flexDirection="column" gap="s4">
          <Text.Normal weight="semibold">{policy.attributes.name}</Text.Normal>
          <Text.Normal ellipsis size="200">
            {i18n.tp('time_tracking.settings.employees_assigned', {
              count: policy.attributes.active_employees.length,
            })}
          </Text.Normal>
        </Box>
        {(policy?.attributes.default || attendanceSetting?.hasFlagsEnabled) && (
          <Box flexDirection="row" gap="s4" width="s300">
            {policy?.attributes.default && (
              <Tag type={Tag.Types.radical} solid>
                {i18n.t('time_tracking.settings.tags.default')}
              </Tag>
            )}
            {attendanceSetting?.attributes.desktop_clockin && (
              <Tag type={Tag.Types.grey}>{i18n.t('time_tracking.settings.tags.desktop')}</Tag>
            )}
            {attendanceSetting?.hasMobileClockin && (
              <Tag type={Tag.Types.grey}>{i18n.t('time_tracking.settings.tags.mobile')}</Tag>
            )}
            {attendanceSetting?.attributes.qr_code_clockin && (
              <Tag type={Tag.Types.grey}>{i18n.t('time_tracking.settings.tags.qr')}</Tag>
            )}
            {attendanceSetting?.attributes.geolocation && (
              <Tag type={Tag.Types.grey}>{i18n.t('time_tracking.settings.tags.geolocation')}</Tag>
            )}
            {attendanceSetting?.attributes.face_recognition && (
              <Tag type={Tag.Types.grey}>
                {i18n.t('time_tracking.settings.tags.face_recognition')}
              </Tag>
            )}
            {attendanceSetting?.attributes.id_clock_shared_device && (
              <Tag type={Tag.Types.grey}>
                {i18n.t('time_tracking.settings.tags.id_clock_shared_device')}
              </Tag>
            )}
          </Box>
        )}
      </Box>
      <Box flexDirection="row" gap="s6">
        <Box gap="s8" alignItems="center" flexDirection="row">
          <Box onClick={(event) => stopPropagation(event)}>
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                {!policy.attributes.default && (
                  <>
                    <PopoverMenu.TouchableOption
                      title={i18n.t('time_tracking.settings.set_as_default')}
                      onClick={submitSetAsDefault(policy)}
                    />
                    <PopoverMenu.TouchableOption
                      title={i18n.t('time_tracking.settings.delete')}
                      onClick={showDeleteModal}
                    />
                  </>
                )}
                {Boolean(policy.attributes.default) && (
                  <Box padding="s24" gap="s4" textAlign="left">
                    <Text color="grey800" weight="medium" size="300">
                      {i18n.t('time_tracking.settings.remove.title')}
                    </Text>
                    <Text.Normal color="grey800" size="200">
                      {i18n.t('time_tracking.settings.remove.description')}
                    </Text.Normal>
                  </Box>
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </Box>
        <Box alignSelf="center">
          <Button.Router aria-label="Go to" to={route} type="secondary" iconName="arrow-right" />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(PolicyCard)
